/**
 * SSE Stream Decoder
 *
 * "data: {...}" formatındaki Server-Sent Events satırlarını parse eden,
 * transport'tan bağımsız ve test edilebilir küçük bir decoder.
 *
 * Özellikler:
 * - Chunk sınırlarında bölünen satırları buffer'da birleştirir
 * - CRLF (\r\n) satır sonlarını destekler
 * - "[DONE]" sentinel'ini ayrı bir event olarak bildirir
 * - Bozuk/parse edilemeyen JSON satırlarını sessizce atlar
 *   (bunlar bağlantı hatası değildir; akış devam eder)
 * - "event:", "id:", ":" (comment) gibi diğer SSE alanlarını yok sayar
 */

export type SSEEvent =
  | { kind: "done" }
  | { kind: "event"; data: Record<string, unknown> & { type?: string } };

const DATA_LINE_PREFIX = "data:";
const DONE_SENTINEL = "[DONE]";

const parseLine = (rawLine: string): SSEEvent | null => {
  // CRLF desteği: satır sonundaki \r'ı at
  const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
  if (line.trim() === "") return null;
  if (!line.startsWith(DATA_LINE_PREFIX)) return null;

  const payload = line.slice(DATA_LINE_PREFIX.length).trim();
  if (payload === "") return null;

  if (payload === DONE_SENTINEL) {
    return { kind: "done" };
  }

  try {
    const data = JSON.parse(payload);
    if (!data || typeof data !== "object") return null;
    return { kind: "event", data };
  } catch {
    // Bozuk/parçalanmış JSON satırı: atla
    return null;
  }
};

export class SSEStreamDecoder {
  private buffer = "";

  /**
   * Ham text chunk'ını işler; tamamlanan satırlardan üretilen event'leri döner.
   * Tamamlanmamış son satır bir sonraki push'a kadar buffer'da tutulur.
   */
  push(chunk: string): SSEEvent[] {
    this.buffer += chunk;
    const lines = this.buffer.split("\n");
    // Son parça incomplete olabilir; buffer'da beklet
    this.buffer = lines.pop() ?? "";

    const events: SSEEvent[] = [];
    for (const line of lines) {
      const event = parseLine(line);
      if (event) events.push(event);
    }
    return events;
  }

  /**
   * Stream bittiğinde çağrılır: buffer'da newline'sız kalan
   * son satır varsa onu da işler.
   */
  flush(): SSEEvent[] {
    const remaining = this.buffer;
    this.buffer = "";
    if (remaining.trim() === "") return [];

    const event = parseLine(remaining);
    return event ? [event] : [];
  }
}
